/**
 * Friendly rendering of a cached action's locator (SelectorStrategy).
 *
 * Single source of truth shared by the cache-debug overlay and the playbook
 * viewer: turns a raw selector object into a primary type, a readable string,
 * and a badge color — never raw JSON in the UI.
 *
 * @module FlowDesigner/selector-format
 */

/** First selector key present, in resolve-preference order. */
export function getPrimarySelectorType(selectors: Record<string, unknown> | null): string | null {
  if (!selectors) return null;
  if (selectors.testId) return 'testId';
  if (selectors.role) return 'role';
  if (selectors.playwrightLocator) return 'playwrightLocator';
  if (selectors.ariaLabel) return 'ariaLabel';
  if (selectors.text) return 'text';
  if (selectors.css) return 'css';
  if (selectors.xpath) return 'xpath';
  return null;
}

/** Human-readable one-liner for the primary selector (e.g. `role: button "Submit"`). */
export function formatSelectorValue(selectors: Record<string, unknown> | null): string {
  if (!selectors) return '—';
  const type = getPrimarySelectorType(selectors);
  if (!type) return '—';

  const value = selectors[type];
  if (typeof value === 'string') return `${type}: "${value}"`;
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if ('role' in obj && 'name' in obj) return `role: ${obj.role} "${obj.name}"`;
    if ('text' in obj) return `text: "${obj.text}"`;
    return `${type}: ${JSON.stringify(value)}`;
  }
  return `${type}: ${String(value)}`;
}

/** Tailwind badge classes per selector type. */
export const selectorTypeBadgeColor: Record<string, string> = {
  testId: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  role: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  playwrightLocator: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  ariaLabel: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  text: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  css: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  xpath: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};
